import type { ComponentPublicInstance, CSSProperties, MaybeRefOrGetter } from "vue";
import { tryOnMounted, tryOnScopeDispose, useEventListener, usePreferredReducedMotion, useResizeObserver } from "@vueuse/core";
import { computed, shallowRef, toValue, watch } from "vue";

/** Whatever a template ref hands over, so callers can register options without casting. */
export type SlidingIndicatorTarget = Element | ComponentPublicInstance | null | undefined;

export interface SlidingIndicatorBounds {
  x: number
  y: number
  width: number
  height: number
}

export interface UseSlidingIndicatorOptions {
  /**
   * Element the indicator is absolutely positioned in. Needs a positioned layout
   * (`relative`) so the indicator origin matches its padding box.
   */
  container: MaybeRefOrGetter<HTMLElement | null | undefined>

  /**
   * Key of the option the indicator sits on, as registered through `setOptionEl`.
   * A key without a registered element hides the indicator, so `null` and `undefined`
   * stay usable as option values.
   */
  activeKey: MaybeRefOrGetter<unknown>

  /**
   * Extra sources to re-measure on, for changes that move the options without
   * resizing them or the container: a replaced option list, a layout switch, a
   * sibling that reflows the row.
   *
   * Refs, getters and plain values are all accepted, and the members of an array
   * are unwrapped one level - so `() => [optionList, comparing]` tracks both of
   * them whether they are refs, computeds or plain values.
   */
  watch?: MaybeRefOrGetter<unknown>

  /**
   * Set to false for states a single indicator cannot represent, such as multi
   * select. The indicator stays hidden so the caller can fall back to its own styling.
   * @default true
   */
  enabled?: MaybeRefOrGetter<boolean>

  /**
   * Keeps the indicator inside the container's content box for the whole motion, for
   * controls whose padding is the visible gutter of a rounded track.
   *
   * The indicator is placed by its four edges instead of a transform, each with its own
   * spring curve. Every edge gets the same bounce as the uncontained indicator, except a
   * leading edge that would pass the gutter: that one stops at the gutter while the
   * trailing edge still bounces, so the bounce against the track's end reads as the
   * indicator squashing against it. The surface stretch is limited to the room left the
   * same way, and re-measures that do not change the selection (a resize, a reflow) place
   * the indicator at once instead of animating from a stale layout.
   * @default false
   */
  contain?: MaybeRefOrGetter<boolean>
}

/** The container's content box, in the same padding-box pixels as the bounds. */
export interface SlidingIndicatorArea {
  left: number
  top: number
  right: number
  bottom: number
}

/** `cubic-bezier(0.34, y, 0.64, 1)` peaks at no more than `0.18 * (y - 1)` past its target (for y up to 1.56). */
const OVERSHOOT_PER_CONTROL_UNIT = 0.18;
const MAX_CONTROL_Y = 1.56;
const MAX_OVERSHOOT_FRACTION = OVERSHOOT_PER_CONTROL_UNIT * (MAX_CONTROL_Y - 1);
const MAX_OVERSHOOT_PX = 12;

const MOVE_DURATION = "340ms";
const SIZE_TRANSITION = "260ms cubic-bezier(0.22, 1, 0.36, 1)";
const SQUASH_TRANSITION = "transform 200ms cubic-bezier(0.33, 1, 0.68, 1)";
const SQUASH_HOLD_MS = 150;
const MIN_SQUASH_TRAVEL_PX = 4;

/** The spring curve whose bounce passes the target by at most `fraction` of the distance travelled. */
function springEasing(fraction: number): string {
  const bounded = Math.min(MAX_OVERSHOOT_FRACTION, Math.max(0, fraction));
  const controlY = 1 + bounded / OVERSHOOT_PER_CONTROL_UNIT;
  return `cubic-bezier(0.34, ${controlY.toFixed(3)}, 0.64, 1)`;
}

/** Keeps the bounce a constant handful of pixels instead of scaling it with the travel. */
function overshootFraction(travel: number): number {
  return Math.min(MAX_OVERSHOOT_FRACTION, MAX_OVERSHOOT_PX / Math.max(travel, 1));
}

function moveEasing(travel: number): string {
  return springEasing(overshootFraction(travel));
}

/**
 * Stretches the surface along the travel and narrows it across. `maxStretch` caps the stretch
 * per axis, for a contained indicator that has only so much room left along its move.
 */
function squashFor(dx: number, dy: number, maxStretch?: { x: number, y: number }): { x: number, y: number } | null {
  const travel = Math.max(dx, dy);

  if (travel < MIN_SQUASH_TRAVEL_PX) {
    return null;
  }

  const amount = Math.min(0.035, Math.max(0.012, travel / 6000));

  return dx >= dy
    ? { x: 1 + Math.min(amount, maxStretch?.x ?? amount), y: 1 - amount * 0.6 }
    : { x: 1 - amount * 0.6, y: 1 + Math.min(amount, maxStretch?.y ?? amount) };
}

/** Bounce of each edge, as a fraction of that edge's own travel. */
export interface SlidingIndicatorEdgeFractions {
  left: number
  right: number
  top: number
  bottom: number
}

export interface ContainedMotion {
  edges: SlidingIndicatorEdgeFractions
  squash: { x: number, y: number } | null
}

interface AxisMotion {
  startFraction: number
  endFraction: number
  /** Stretch the surface may add on this axis without leaving the area anywhere along the move. */
  maxStretch: number
}

/** The bounce an edge keeps when it may only pass its target by the room left before `min` or `max`. */
function edgeFraction(delta: number, target: number, min: number, max: number, fraction: number): number {
  if (delta === 0) {
    return fraction;
  }

  const room = Math.max(0, delta < 0 ? target - min : max - target);
  return Math.min(fraction, room / Math.abs(delta));
}

/**
 * Bounces one axis (left and right, or top and bottom) of a contained move.
 *
 * Each edge moves as `start + progress * delta` and its spring passes the target by at most
 * `fraction * |delta|`. An edge moving towards the area's boundary may only pass its target by
 * the room left before that boundary; an edge moving inward squeezes the indicator, which may
 * lose at most half its new length so it never collapses mid-flight.
 */
function containAxis(
  previousStart: number,
  previousLength: number,
  nextStart: number,
  nextLength: number,
  min: number,
  max: number,
  fraction: number,
): AxisMotion {
  const nextEnd = nextStart + nextLength;
  const startDelta = nextStart - previousStart;
  const endDelta = nextEnd - (previousStart + previousLength);

  let startFraction = edgeFraction(startDelta, nextStart, min, max, fraction);
  let endFraction = edgeFraction(endDelta, nextEnd, min, max, fraction);

  // A start edge moving forward and an end edge moving back both pull the edges together.
  const squeeze = (startDelta > 0 ? startFraction * startDelta : 0) + (endDelta < 0 ? endFraction * -endDelta : 0);
  if (squeeze > nextLength / 2) {
    const scale = nextLength / 2 / squeeze;
    startFraction = startDelta > 0 ? startFraction * scale : startFraction;
    endFraction = endDelta < 0 ? endFraction * scale : endFraction;
  }

  // Each edge only travels between where it started and its bounce peak, so that span bounds the
  // indicator for the whole move; the surface stretch has to fit in what is left around it.
  const startPeak = nextStart + startFraction * startDelta;
  const endPeak = nextEnd + endFraction * endDelta;
  const outerStart = Math.min(previousStart, startPeak);
  const outerEnd = Math.max(previousStart + previousLength, endPeak);
  const slack = Math.max(0, Math.min(outerStart - min, max - outerEnd));
  const widest = Math.max(previousLength, nextLength, endPeak - startPeak, 1);

  return {
    startFraction,
    endFraction,
    maxStretch: (2 * slack) / widest,
  };
}

/**
 * The contained version of a move: the same bounce the uncontained indicator gets, per edge,
 * cut back only where an edge or the surface stretch would pass the area.
 */
export function containedMotion(
  previous: SlidingIndicatorBounds,
  next: SlidingIndicatorBounds,
  area: SlidingIndicatorArea,
): ContainedMotion {
  const dx = Math.abs(next.x - previous.x);
  const dy = Math.abs(next.y - previous.y);
  const fraction = overshootFraction(Math.max(dx, dy));

  const horizontal = containAxis(previous.x, previous.width, next.x, next.width, area.left, area.right, fraction);
  const vertical = containAxis(previous.y, previous.height, next.y, next.height, area.top, area.bottom, fraction);

  const squash = squashFor(dx, dy, { x: horizontal.maxStretch, y: vertical.maxStretch });

  return {
    edges: {
      left: horizontal.startFraction,
      right: horizontal.endFraction,
      top: vertical.startFraction,
      bottom: vertical.endFraction,
    },
    squash,
  };
}

/** Reading the members too, so an array of refs tracks instead of handing back a fresh array of refs. */
function readWatchSource(source: MaybeRefOrGetter<unknown>): unknown {
  const value = toValue(source);

  if (!Array.isArray(value)) {
    return value;
  }

  return value.map((member: unknown) => toValue(member));
}

function resolveTarget(target: SlidingIndicatorTarget): HTMLElement | null {
  if (target instanceof HTMLElement) {
    return target;
  }

  if (target && typeof target === "object" && "$el" in target) {
    const root = target.$el;
    return root instanceof HTMLElement ? root : null;
  }

  return null;
}

function isSameBounds(a: SlidingIndicatorBounds | null, b: SlidingIndicatorBounds | null): boolean {
  if (!a || !b) {
    return a === b;
  }

  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

function pixels(value: string): number {
  return Number.parseFloat(value) || 0;
}

function contentArea(container: HTMLElement): SlidingIndicatorArea {
  const style = getComputedStyle(container);

  return {
    left: pixels(style.paddingLeft),
    top: pixels(style.paddingTop),
    right: container.clientWidth - pixels(style.paddingRight),
    bottom: container.clientHeight - pixels(style.paddingBottom),
  };
}

/**
 * Measures the active option of a segmented control and slides a decorative
 * indicator onto it, instead of letting the selected styling snap into place.
 *
 * The indicator is rendered by the caller as two nested elements inside `container`:
 * an absolutely positioned wrapper carrying `indicatorStyle` (position and size) and
 * a child carrying `surfaceStyle` (the squash while travelling). It only appears once
 * `bounds` is measured, so it is never animated in on first paint, and it stays hidden
 * while the control has no measurable size - a control in a collapsed panel or a closed
 * dialog measures itself again through the resize observer once it becomes visible.
 */
export function useSlidingIndicator(options: UseSlidingIndicatorOptions) {
  const optionEls = new Map<unknown, HTMLElement>();
  const observedEls = shallowRef<HTMLElement[]>([]);
  const bounds = shallowRef<SlidingIndicatorBounds | null>(null);
  const squash = shallowRef<{ x: number, y: number } | null>(null);
  const travel = shallowRef(0);
  // Contained mode only: the edge curves of the running move (null places the indicator at once),
  // and the container's padding box size the right and bottom edges are placed from.
  const containedEdges = shallowRef<SlidingIndicatorEdgeFractions | null>(null);
  const frame = shallowRef({ width: 0, height: 0 });
  const preferredMotion = usePreferredReducedMotion();
  const animated = computed(() => preferredMotion.value !== "reduce");
  const contained = computed(() => toValue(options.contain ?? false));

  let measuredKey: unknown;

  let squashTimer: ReturnType<typeof setTimeout> | undefined;

  // Re-registering the same elements must not churn the resize observer, since every
  // render of the caller re-invokes its template refs.
  function setOptionEl(key: unknown, target: SlidingIndicatorTarget) {
    const registered = optionEls.get(key);
    const el = resolveTarget(target);

    if (el) {
      if (registered === el) {
        return;
      }

      optionEls.set(key, el);
    } else {
      if (!registered) {
        return;
      }

      optionEls.delete(key);
    }

    observedEls.value = [...optionEls.values()];
  }

  function measureBounds(): SlidingIndicatorBounds | null {
    if (!toValue(options.enabled ?? true)) {
      return null;
    }

    const container = toValue(options.container);
    const target = optionEls.get(toValue(options.activeKey));

    if (!container || !target) {
      return null;
    }

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    if (targetRect.width === 0 || targetRect.height === 0) {
      return null;
    }

    // Client rects include ancestor transforms, such as a dialog that scales in while it opens. The
    // resize observer never fires when that transform ends, so convert to the container's own
    // (untransformed) pixels; otherwise the indicator keeps the size it had mid-animation.
    const scaleX = container.offsetWidth > 0 ? containerRect.width / container.offsetWidth : 1;
    const scaleY = container.offsetHeight > 0 ? containerRect.height / container.offsetHeight : 1;

    return {
      x: (targetRect.left - containerRect.left) / scaleX - container.clientLeft,
      y: (targetRect.top - containerRect.top) / scaleY - container.clientTop,
      width: targetRect.width / scaleX,
      height: targetRect.height / scaleY,
    };
  }

  function measure() {
    const next = measureBounds();
    const previous = bounds.value;
    const key = toValue(options.activeKey);
    const selectionChanged = key !== measuredKey;

    measuredKey = key;

    if (isSameBounds(previous, next)) {
      return;
    }

    const container = toValue(options.container);
    const motion = contained.value && container && next && previous && selectionChanged
      ? containedMotion(previous, next, contentArea(container))
      : null;

    if (contained.value) {
      containedEdges.value = motion?.edges ?? null;
      frame.value = { width: container?.clientWidth ?? 0, height: container?.clientHeight ?? 0 };
    }

    bounds.value = next;

    if (!next || !previous || !animated.value) {
      return;
    }

    if (contained.value && !motion) {
      return;
    }

    const dx = Math.abs(next.x - previous.x);
    const dy = Math.abs(next.y - previous.y);

    travel.value = Math.max(dx, dy);
    squash.value = motion ? motion.squash : squashFor(dx, dy);
    clearTimeout(squashTimer);
    squashTimer = setTimeout(() => {
      squash.value = null;
    }, SQUASH_HOLD_MS);
  }

  function containedTransition(): string {
    const edges = containedEdges.value;

    if (!edges) {
      return "none";
    }

    return (["left", "right", "top", "bottom"] as const)
      .map(edge => `${edge} ${MOVE_DURATION} ${springEasing(edges[edge])}`)
      .join(", ");
  }

  const indicatorStyle = computed<CSSProperties>(() => {
    const current = bounds.value;

    if (!current) {
      return {};
    }

    if (contained.value) {
      return {
        left: `${current.x}px`,
        top: `${current.y}px`,
        right: `${frame.value.width - current.x - current.width}px`,
        bottom: `${frame.value.height - current.y - current.height}px`,
        transition: animated.value ? containedTransition() : "none",
      };
    }

    const transition = [
      `transform ${MOVE_DURATION} ${moveEasing(travel.value)}`,
      `width ${SIZE_TRANSITION}`,
      `height ${SIZE_TRANSITION}`,
    ].join(", ");

    return {
      width: `${current.width}px`,
      height: `${current.height}px`,
      transform: `translate3d(${current.x}px, ${current.y}px, 0)`,
      transition: animated.value ? transition : "none",
    };
  });

  const surfaceStyle = computed<CSSProperties>(() => {
    const current = squash.value;

    return {
      transform: current ? `scale(${current.x}, ${current.y})` : "scale(1, 1)",
      transition: animated.value ? SQUASH_TRANSITION : "none",
    };
  });

  useResizeObserver(
    () => [toValue(options.container), ...observedEls.value].filter((el): el is HTMLElement => Boolean(el)),
    () => measure(),
  );

  useEventListener("resize", () => measure(), { passive: true });

  watch(
    () => [
      toValue(options.container),
      toValue(options.activeKey),
      toValue(options.enabled ?? true),
      readWatchSource(options.watch),
    ],
    () => measure(),
    { flush: "post" },
  );

  tryOnMounted(() => measure());

  tryOnScopeDispose(() => clearTimeout(squashTimer));

  return {
    bounds,
    indicatorStyle,
    surfaceStyle,
    animated,
    setOptionEl,
    measure,
  };
}
